import React, { useState, useEffect } from "react";
import {
  useHistory,
  useParams,
  Route,
  Switch,
  useLocation,
} from "react-router-dom";
import {
  Users,
  MapPin,
  Calendar,
  Settings,
  FileText,
  Menu,
  X,
  BarChart3,
  Home,
  ChevronLeft,
  LogOut,
} from "lucide-react";
import BossEmployee from "./BossMainPageComps/BossEmployee";
import BossPlaces from "./BossMainPageComps/BossPlaces";
import BossInvoices from "./BossMainPageComps/BossInvoices";

// Import your page components (you'll need to create these)
// import BossHome from "./BossHome";
// import BossEmployee from "./BossEmployee";
// import BossPlaces from "./BossPlaces";
// import BossByDate from "./BossByDate";
// import BossSetting from "./BossSetting";

// Placeholder components - replace with your actual components
const BossHome = () => (
  <div className="p-8">
    <h1 className="text-2xl font-bold">Home</h1>
  </div>
);
// const BossEmployee = () => (
//   <div className="p-8">
//     <h1 className="text-2xl font-bold">Employees</h1>
//   </div>
// );
// const BossPlaces = () => (
//   <div className="p-8">
//     <h1 className="text-2xl font-bold">Places</h1>
//   </div>
// );
const BossByDate = () => (
  <div className="p-8">
    <h1 className="text-2xl font-bold">General</h1>
  </div>
);
const BossSetting = () => (
  <div className="p-8">
    <h1 className="text-2xl font-bold">Settings</h1>
  </div>
);

export default function BossMainPage() {
  const history = useHistory();
  const location = useLocation();
  const { BossId } = useParams();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    // Check if user is logged in
    const user = localStorage.getItem("user");
    if (!user) {
      history.replace("/login");
      return;
    }
    setUserData(JSON.parse(user));
  }, [history]);

  const handleLogout = () => {
    localStorage.removeItem("user");
    history.replace("/login");
  };

  const menuItems = [
    {
      path: `/boss-main-page/${BossId}/employees`,
      icon: Users,
      label: "Employees",
      color: "text-blue-600",
    },
    {
      path: `/boss-main-page/${BossId}/places`,
      icon: MapPin,
      label: "Places",
      color: "text-green-600",
    },
    {
      path: `/boss-main-page/${BossId}/general`,
      icon: Calendar,
      label: "General",
      color: "text-purple-600",
    },
    {
      path: `/boss-main-page/${BossId}/invoices`, // 👈 new
      icon: FileText,
      label: "Invoices",
      color: "text-orange-600",
    },
    {
      path: `/boss-main-page/${BossId}/settings`,
      icon: Settings,
      label: "Settings",
      color: "text-gray-600",
    },
  ];

  const isActive = (path) => location.pathname === path;

  // Redirect to employees by default
  useEffect(() => {
    if (location.pathname === `/boss-main-page/${BossId}`) {
      history.replace(`/boss-main-page/${BossId}/employees`);
    }
  }, [location.pathname, BossId, history]);

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      <style>{`
        @keyframes slideIn {
          from { transform: translateX(-100%); }
          to { transform: translateX(0); }
        }
        .animate-slide-in {
          animation: slideIn 0.3s ease-out;
        }
      `}</style>

      {/* Sidebar - Desktop */}
      <aside
        className={`hidden lg:flex flex-col bg-white border-r border-gray-200 transition-all duration-300 ${
          sidebarOpen ? "w-64" : "w-20"
        }`}
      >
        {/* Logo Section */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          {sidebarOpen ? (
            <>
              <div className="flex items-center space-x-2">
                <BarChart3 className="w-8 h-8 text-blue-600" />
                <span className="text-xl font-bold text-gray-900">
                  NeholTorem
                </span>
              </div>
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 rounded-lg hover:bg-gray-100 transition"
              >
                <ChevronLeft className="w-5 h-5 text-gray-600 transition-transform" />
              </button>
            </>
          ) : (
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="w-full flex justify-center p-2 rounded-lg hover:bg-gray-100 transition"
            >
              <BarChart3 className="w-8 h-8 text-blue-600" />
            </button>
          )}
        </div>

        {/* User Info */}
        {sidebarOpen && userData && (
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 font-semibold text-lg">
                  {userData.name?.charAt(0).toUpperCase() || "U"}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">
                  {userData.name || "User"}
                </p>
                <p className="text-xs text-gray-500">Owner Account</p>
              </div>
            </div>
          </div>
        )}

        {/* Collapsed User Avatar */}
        {!sidebarOpen && userData && (
          <div className="p-4 border-b border-gray-200 flex justify-center">
            <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-blue-600 font-semibold text-lg">
                {userData.name?.charAt(0).toUpperCase() || "U"}
              </span>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2 overflow-y-auto">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <div key={item.path} className="relative group">
                <button
                  onClick={() => history.push(item.path)}
                  className={`w-full flex items-center ${
                    sidebarOpen ? "space-x-3" : "justify-center"
                  } px-4 py-3 rounded-lg transition-all ${
                    active
                      ? "bg-blue-50 text-blue-600 shadow-sm"
                      : "text-gray-700 hover:bg-gray-100"
                  }`}
                >
                  <Icon
                    className={`w-5 h-5 flex-shrink-0 ${
                      active ? "text-blue-600" : item.color
                    }`}
                  />
                  {sidebarOpen && (
                    <span className="font-medium">{item.label}</span>
                  )}
                </button>

                {/* Tooltip for collapsed sidebar */}
                {!sidebarOpen && (
                  <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 pointer-events-none">
                    {item.label}
                    <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-gray-200">
          <div className="relative group">
            <button
              onClick={handleLogout}
              className={`w-full flex items-center ${
                sidebarOpen ? "space-x-3" : "justify-center"
              } px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition`}
            >
              <LogOut className="w-5 h-5 flex-shrink-0" />
              {sidebarOpen && <span className="font-medium">Logout</span>}
            </button>

            {/* Tooltip for collapsed sidebar */}
            {!sidebarOpen && (
              <div className="absolute left-full ml-2 top-1/2 -translate-y-1/2 px-3 py-2 bg-gray-900 text-white text-sm rounded-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50 pointer-events-none">
                Logout
                <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900"></div>
              </div>
            )}
          </div>
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile Sidebar */}
      <aside
        className={`lg:hidden fixed inset-y-0 left-0 w-64 bg-white border-r border-gray-200 z-50 transform transition-transform duration-300 ${
          mobileMenuOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        {/* Logo Section */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-gray-200">
          <div className="flex items-center space-x-2">
            <BarChart3 className="w-8 h-8 text-blue-600" />
            <span className="text-xl font-bold text-gray-900">NeholTorem</span>
          </div>
          <button
            onClick={() => setMobileMenuOpen(false)}
            className="p-2 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        {/* User Info */}
        {userData && (
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                <span className="text-blue-600 font-semibold text-lg">
                  {userData.name?.charAt(0).toUpperCase() || "U"}
                </span>
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900">
                  {userData.name || "User"}
                </p>
                <p className="text-xs text-gray-500">Owner Account</p>
              </div>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.path);
            return (
              <button
                key={item.path}
                onClick={() => {
                  history.push(item.path);
                  setMobileMenuOpen(false);
                }}
                className={`w-full flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
                  active
                    ? "bg-blue-50 text-blue-600 shadow-sm"
                    : "text-gray-700 hover:bg-gray-100"
                }`}
              >
                <Icon
                  className={`w-5 h-5 ${active ? "text-blue-600" : item.color}`}
                />
                <span className="font-medium">{item.label}</span>
              </button>
            );
          })}
        </nav>

        {/* Logout Button */}
        <div className="p-4 border-t border-gray-200">
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition"
          >
            <LogOut className="w-5 h-5" />
            <span className="font-medium">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
            >
              <Menu className="w-6 h-6 text-gray-600" />
            </button>
            <h1 className="text-xl font-bold text-gray-900">
              {menuItems.find((item) => isActive(item.path))?.label ||
                "Dashboard"}
            </h1>
          </div>

          {/* Header Actions */}
          <div className="flex items-center space-x-4">
            {/* Notification Bell */}
            <button className="relative p-2 rounded-lg hover:bg-gray-100 transition">
              <svg
                className="w-6 h-6 text-gray-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                />
              </svg>
              <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
            </button>

            {/* User Avatar - Mobile */}
            <div className="lg:hidden w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center">
              <span className="text-blue-600 font-semibold text-sm">
                {userData?.name?.charAt(0).toUpperCase() || "U"}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto bg-gray-50">
          <Switch>
            <Route
              path="/boss-main-page/:BossId/employees"
              component={BossEmployee}
            />
            <Route
              path="/boss-main-page/:BossId/places"
              component={BossPlaces}
            />
            <Route
              path="/boss-main-page/:BossId/general"
              component={BossByDate}
            />
            <Route
              path="/boss-main-page/:BossId/invoices"
              component={BossInvoices}
            />
            <Route
              path="/boss-main-page/:BossId/settings"
              component={BossSetting}
            />
          </Switch>
        </main>
      </div>
    </div>
  );
}
